import '~/assets/styles/index.scss';

import React, { useEffect } from 'react';
import { GeoLocationProvider } from '~/app/context';
import { runCtmWhenReady } from '~/shared/lib/run-ctm';
import { CookiePreferencesPanel } from '~/widgets/consent/cookie-preferences-panel';
import { getConsent, onConsentChanged } from '~/shared/consent/consent-manager';

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'cq_cmp',
  'cq_con',
  'cq_med',
  'cq_net',
  'cq_plt',
  'cq_src',
  'cq_term',
  'city',
];
const FIRST_TOUCH_KEY = 'analytics_first_touch_v1';
const LAST_TOUCH_KEY = 'analytics_last_touch_v1';
const ATTR_COOKIE_PREFIX = 'ctm_attr_';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

const readAttribution = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const url = new URL(window.location.href);
  const result = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = url.searchParams.get(key);
    if (value) {
      result[key] = value;
    }
  });

  result.landing_url = url.href;
  result.landing_path = `${url.pathname}${url.search}`;
  result.referrer = document.referrer || '';

  return result;
};

const hasPaidParams = (attribution) => {
  return Boolean(
    attribution.gclid ||
      attribution.msclkid ||
      attribution.fbclid ||
      attribution.utm_source ||
      attribution.cq_src ||
      attribution.cq_cmp
  );
};

const setCookie = (name, value, maxAge = COOKIE_MAX_AGE) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
};

const setAttributionCookies = (firstTouch, lastTouch) => {
  const writeObject = (prefix, source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      setCookie(`${ATTR_COOKIE_PREFIX}${prefix}_${key}`, String(value));
    });
  };

  writeObject('first', firstTouch);
  writeObject('last', lastTouch);
};

const persistAttribution = (attribution) => {
  try {
    const firstTouchRaw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (!firstTouchRaw) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attribution));
    }
    window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(attribution));
  } catch (error) {
    // No-op
  }
};

const readPersistedAttribution = () => {
  try {
    const firstTouch = JSON.parse(window.localStorage.getItem(FIRST_TOUCH_KEY) || '{}');
    const lastTouch = JSON.parse(window.localStorage.getItem(LAST_TOUCH_KEY) || '{}');
    return { firstTouch, lastTouch };
  } catch (error) {
    return { firstTouch: {}, lastTouch: {} };
  }
};

const primeDataLayer = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const currentAttribution = readAttribution();
  persistAttribution(currentAttribution);
  const { firstTouch, lastTouch } = readPersistedAttribution();
  setAttributionCookies(firstTouch, lastTouch);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'traffic_context_ready',
    page_url: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || '',
    source_data: {
      first_touch: firstTouch,
      last_touch: lastTouch,
    },
    first_touch: firstTouch,
    last_touch: lastTouch,
  });

  return currentAttribution;
};

const loadGtm = () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__gtmLoaded) {
    return;
  }

  const gtmId = process.env.GTM_CODE;
  if (!gtmId) {
    return;
  }

  window.__gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  script.onload = () => {
    if (getConsent().ads) {
      runCtmWhenReady();
    }
  };
  document.head.appendChild(script);
};

const ThirdPartyDeferredLoader = () => {
  useEffect(() => {
    const attribution = primeDataLayer();
    let idleId = null;
    let timeoutId = null;

    const triggerLoad = () => {
      loadGtm();
      if (getConsent().ads) {
        runCtmWhenReady();
      }
      window.removeEventListener('scroll', triggerLoad);
      window.removeEventListener('pointerdown', triggerLoad);
      window.removeEventListener('keydown', triggerLoad);
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    window.addEventListener('scroll', triggerLoad, { once: true, passive: true });
    window.addEventListener('pointerdown', triggerLoad, { once: true, passive: true });
    window.addEventListener('keydown', triggerLoad, { once: true });

    if (hasPaidParams(attribution)) {
      timeoutId = setTimeout(triggerLoad, 50);
    } else if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(triggerLoad, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(triggerLoad, 2500);
    }

    const onPhoneClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest('a[href^="tel:"]');
      if (!anchor) {
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'phone_click',
        phone_href: anchor.getAttribute('href') || '',
      });
      triggerLoad();
    };

    document.addEventListener('click', onPhoneClick);

    const unsubscribeConsent = onConsentChanged((event) => {
      if (event.detail?.ads) {
        runCtmWhenReady();
      }
    });

    return () => {
      window.removeEventListener('scroll', triggerLoad);
      window.removeEventListener('pointerdown', triggerLoad);
      window.removeEventListener('keydown', triggerLoad);
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('click', onPhoneClick);
      unsubscribeConsent();
    };
  }, []);

  return null;
};

// Disable native scroll restoration so a reload after jumping to an in-page
// anchor (e.g. #footer-legal) does not dump the visitor back at the bottom.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const stripHashFromUrl = () => {
  if (typeof window === 'undefined' || !window.location.hash) {
    return;
  }
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
};

const getHashTarget = (hash) => {
  if (!hash || hash === '#') {
    return null;
  }
  try {
    return document.getElementById(decodeURIComponent(hash.replace(/^#/, '')));
  } catch (error) {
    return document.getElementById(hash.replace(/^#/, ''));
  }
};

const getScrollBlock = (target) => {
  if (target && (target.id === 'footer-legal' || target.closest('footer'))) {
    return 'end';
  }
  return 'start';
};

const getCurrentScrollTop = () => window.pageYOffset || document.documentElement.scrollTop || 0;

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const getAnchorScrollTop = (target) => {
  const rect = target.getBoundingClientRect();
  const current = getCurrentScrollTop();
  let next;
  if (getScrollBlock(target) === 'end') {
    next = current + rect.bottom - window.innerHeight;
  } else {
    const marginTop = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
    next = current + rect.top - marginTop;
  }
  const max = Math.max(
    0,
    (document.documentElement.scrollHeight || document.body.scrollHeight) - window.innerHeight
  );
  return Math.max(0, Math.min(next, max));
};

const setNativeScrollBehavior = (value) => {
  document.documentElement.style.scrollBehavior = value;
};

// Custom ease instead of CSS/scrollIntoView smooth: those undershoot and
// stop in the middle of the page. Destination is recomputed every frame so
// lazy images pushing the footer down still get followed.
const animateScrollToHash = (hash, onDone) => {
  const html = document.documentElement;
  const previousBehavior = html.style.scrollBehavior;
  setNativeScrollBehavior('auto');

  const finish = () => {
    html.style.scrollBehavior = previousBehavior;
    onDone?.();
  };

  const target = getHashTarget(hash);
  if (!target) {
    finish();
    return () => {};
  }

  const startY = getCurrentScrollTop();
  const distance = Math.abs(getAnchorScrollTop(target) - startY);
  if (distance < 2) {
    finish();
    return () => {};
  }

  const duration = Math.min(1100, Math.max(600, distance * 0.5));
  let rafId = 0;
  let stopped = false;
  const startTime = performance.now();

  const tick = (now) => {
    if (stopped) {
      return;
    }
    const el = getHashTarget(hash);
    if (!el) {
      finish();
      return;
    }
    const t = Math.min(1, (now - startTime) / duration);
    const destY = getAnchorScrollTop(el);
    window.scrollTo(0, startY + (destY - startY) * easeInOutCubic(t));
    if (t < 1) {
      rafId = requestAnimationFrame(tick);
    } else {
      finish();
    }
  };

  rafId = requestAnimationFrame(tick);

  return () => {
    stopped = true;
    cancelAnimationFrame(rafId);
    html.style.scrollBehavior = previousBehavior;
  };
};

// After the ease, keep watching layout until it settles — a small snap only
// if lazy sections shift the footer. Backs off on any visitor input (scroll, tap,
// click, key) so it never fights the scroll-to-top button.
const TICK_MS = 150;
const MAX_MS = 10000;
const REQUIRED_STABLE_TICKS = 6; // ~900ms with no height change

const runHashScrollCorrection = (hash) => {
  const targetHash = hash || (typeof window !== 'undefined' ? window.location.hash : '');
  if (!targetHash) {
    return () => {};
  }

  stripHashFromUrl();

  let stopped = false;
  let lastHeight = -1;
  let stableTicks = 0;
  let elapsedMs = 0;
  let animating = true;
  let cancelAnimate = () => {};

  const stop = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    cancelAnimate();
    clearInterval(intervalId);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('pointerdown', stop);
    window.removeEventListener('keydown', stop);
  };

  cancelAnimate = animateScrollToHash(targetHash, () => {
    animating = false;
  });

  const intervalId = setInterval(() => {
    elapsedMs += TICK_MS;
    if (stopped) {
      return;
    }

    const target = getHashTarget(targetHash);
    const height = document.body.scrollHeight;
    if (height === lastHeight) {
      stableTicks += 1;
    } else {
      stableTicks = 0;
      lastHeight = height;
    }

    if (!animating && target) {
      const drift = getAnchorScrollTop(target) - getCurrentScrollTop();
      if (Math.abs(drift) > 80) {
        animating = true;
        cancelAnimate = animateScrollToHash(targetHash, () => {
          animating = false;
        });
      } else if (Math.abs(drift) > 2) {
        setNativeScrollBehavior('auto');
        window.scrollTo(0, getAnchorScrollTop(target));
      }
    }

    if ((target && stableTicks >= REQUIRED_STABLE_TICKS && !animating) || elapsedMs >= MAX_MS) {
      stop();
    }
  }, TICK_MS);

  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('pointerdown', stop, { passive: true });
  window.addEventListener('keydown', stop);

  return stop;
};

const HashScrollFix = () => {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let cancelCurrent = () => {};

    if (window.location.hash) {
      cancelCurrent = runHashScrollCorrection(window.location.hash);
    } else {
      window.scrollTo(0, 0);
    }

    const onHashChange = () => {
      cancelCurrent();
      cancelCurrent = runHashScrollCorrection(window.location.hash);
    };

    // Keep the hash out of the URL on same-page clicks so a later reload
    // is a plain visit to the top, not a jump back to the footer.
    const onInPageAnchorClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const targetEl = event.target;
      if (!(targetEl instanceof Element)) {
        return;
      }
      const anchor = targetEl.closest('a[href^="#"]');
      if (!anchor) {
        return;
      }
      const href = anchor.getAttribute('href');
      const target = getHashTarget(href);
      if (!target) {
        return;
      }
      event.preventDefault();
      cancelCurrent();
      cancelCurrent = runHashScrollCorrection(href);

      // Skip links target a focusable region (tabindex="-1"); move focus there
      // too, or the next Tab would start from the top of the page again.
      if (target.getAttribute('tabindex') === '-1') {
        target.focus({ preventScroll: true });
      }
    };

    window.addEventListener('hashchange', onHashChange);
    document.addEventListener('click', onInPageAnchorClick);

    return () => {
      cancelCurrent();
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener('click', onInPageAnchorClick);
    };
  }, []);

  return null;
};

export const shouldUpdateScroll = ({ routerProps }) => {
  if (routerProps?.location?.hash) {
    return false;
  }
  return [0, 0];
};

export const wrapRootElement = ({ element }) => {
  return (
    <GeoLocationProvider>
      <ThirdPartyDeferredLoader />
      <CookiePreferencesPanel />
      <HashScrollFix />
      {element}
    </GeoLocationProvider>
  );
};
